/**
 * Date Utility Functions
 * REQ-030: Frontend Portal Split - Shared Utils Package
 */

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import updateLocale from 'dayjs/plugin/updateLocale';

// Initialize dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);

// Configure relative time format
dayjs.updateLocale('en', {
  relativeTime: {
    future: 'in %s',
    past: '%s ago',
    s: 'a few sec',
    m: 'a min',
    mm: '%d min',
    h: 'an hour',
    hh: '%d hours',
    d: 'a day',
    dd: '%d days',
    M: 'a month',
    MM: '%d months',
    y: 'a year',
    yy: '%d years',
  },
});

/**
 * Re-export configured dayjs instance
 */
export { dayjs };

/**
 * Format a date to a localized string
 * @param date - Date to format (Date, string, or timestamp)
 * @param format - dayjs format string
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  format: string = 'MMM D, YYYY'
): string {
  if (!date) return '';
  return dayjs(date).format(format);
}

/**
 * Format a date with time
 * @param date - Date to format
 * @param format - dayjs format string
 * @returns Formatted date string with time
 */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  format: string = 'MMM D, YYYY h:mm A'
): string {
  if (!date) return '';
  return dayjs(date).format(format);
}

/**
 * Get relative time string (e.g., "2 hours ago")
 * @param date - Date to compare against now
 * @returns Relative time string
 */
export function formatRelativeTime(
  date: Date | string | number | null | undefined
): string {
  if (!date) return '';
  return dayjs(date).fromNow();
}

/**
 * Check if a date is today
 * @param date - Date to check
 * @returns True if date is today
 */
export function isToday(date: Date | string | number): boolean {
  return dayjs(date).isSame(dayjs(), 'day');
}

/**
 * Check if a date is in the past
 * @param date - Date to check
 * @returns True if date is in the past
 */
export function isPast(date: Date | string | number): boolean {
  return dayjs(date).isBefore(dayjs());
}

/**
 * Check if a date is in the future
 * @param date - Date to check
 * @returns True if date is in the future
 */
export function isFuture(date: Date | string | number): boolean {
  return dayjs(date).isAfter(dayjs());
}

/**
 * Get the difference between two dates in specified unit
 * @param date1 - First date
 * @param date2 - Second date (defaults to now)
 * @param unit - Unit for difference (day, hour, minute, etc.)
 * @returns Difference in specified unit
 */
export function dateDiff(
  date1: Date | string | number,
  date2: Date | string | number = new Date(),
  unit: 'day' | 'hour' | 'minute' | 'second' | 'week' | 'month' | 'year' = 'day'
): number {
  return dayjs(date1).diff(dayjs(date2), unit);
}

/**
 * Parse ISO date string to Date object
 * @param isoString - ISO date string
 * @returns Date object or null if invalid
 */
export function parseISODate(isoString: string): Date | null {
  const parsed = dayjs(isoString);
  return parsed.isValid() ? parsed.toDate() : null;
}
